/**
 * AI 多角度弹窗
 *
 * 功能：
 *  - 设置左右角度（-60~60）、俯仰角度（-45~45）、镜头距离（1~10）、广角/标准
 *  - CSS perspective 实时预览变换效果
 *  - 将角度参数拼接到 prompt 中，复用现有图片生成 API
 *  - 生成后创建新图片节点 + 自动连线
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, RotateCcw, Wand2 } from 'lucide-react';
import { boardActions } from './state/boardStore';
import { makeNode, nid } from './utils';
import { normalizeImageUrlForApi } from './state/upstream';
import { runImageGeneration } from './generators/imageGenerator';
import type { CanvasNode, GenerateImageNodeData, ImageNodeData } from './types';
import toast from '../../components/Toast';
import { resolveBrowserMediaUrl } from '../../utils/storageUrl';

interface AngleDialogProps {
  sourceNode: CanvasNode<ImageNodeData>;
  onClose: () => void;
}

export function AngleDialog({ sourceNode, onClose }: AngleDialogProps) {
  const [horizontalAngle, setHorizontalAngle] = useState(0); // -60 ~ 60
  const [verticalAngle, setVerticalAngle] = useState(0);     // -45 ~ 45
  const [lensDistance, setLensDistance] = useState(5);        // 1 ~ 10
  const [lensType, setLensType] = useState<'standard' | 'wide'>('standard');
  const [generating, setGenerating] = useState(false);

  // CSS perspective 预览
  const perspective = 400 / (lensDistance / 5);
  const rotateY = horizontalAngle;
  const rotateX = -verticalAngle;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // 构建角度描述 prompt
      const angleParts: string[] = [];
      if (horizontalAngle !== 0) {
        const dir = horizontalAngle > 0 ? 'right' : 'left';
        angleParts.push(`camera angle ${dir} ${Math.abs(horizontalAngle)}°`);
      }
      if (verticalAngle !== 0) {
        const dir = verticalAngle > 0 ? 'up' : 'down';
        angleParts.push(`camera pitch ${dir} ${Math.abs(verticalAngle)}°`);
      }
      if (lensDistance !== 5) {
        angleParts.push(`lens distance ${lensDistance}`);
      }
      if (lensType === 'wide') {
        angleParts.push('wide angle lens');
      }

      const anglePrompt = angleParts.join(', ');
      const basePrompt = sourceNode.data.prompt || '';
      const finalPrompt = basePrompt
        ? `${basePrompt}, ${anglePrompt}`
        : anglePrompt;

      // 创建生图配置节点 + 连线
      const genNode = makeNode('generate_image', {
        x: sourceNode.x + sourceNode.width + 60,
        y: sourceNode.y,
      }, {
        // 预填角度提示词，用户可见可改；
        // runImageGeneration 已接收 finalPrompt（含基础 prompt），
        // 此处只填角度部分，避免 aggregateUpstream 再次追加基础 prompt 导致重复
        prompt: anglePrompt,
        mode: 'img2img',
        aspectRatio: 'auto',
        quality: 'low',
        count: 1,
      });
      boardActions.addNode(genNode);
      boardActions.addEdge({
        id: nid(),
        sourceNodeId: sourceNode.id,
        targetNodeId: genNode.id,
        sourceHandle: 'right',
        targetHandle: 'left',
      });

      // 触发生成
      await runImageGeneration({
        generateNode: genNode as CanvasNode<GenerateImageNodeData>,
        prompt: finalPrompt,
        referenceImages: (() => {
          const n = sourceNode.data.imageUrl ? normalizeImageUrlForApi(sourceNode.data.imageUrl) : '';
          return n ? [n] : [];
        })(),
      });

      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '生成失败');
    } finally {
      setGenerating(false);
    }
  };

  const resetParams = () => {
    setHorizontalAngle(0);
    setVerticalAngle(0);
    setLensDistance(5);
    setLensType('standard');
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="flex w-[860px] max-w-[90vw] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-slate-900">AI 多角度</h3>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex">
          {/* 左侧：预览 */}
          <div className="flex flex-1 items-center justify-center bg-slate-100 p-8" style={{ perspective: 800 }}>
            <div
              className="overflow-hidden rounded-lg shadow-xl transition-transform duration-200"
              style={{
                width: 280,
                height: 280,
                transform: `perspective(${perspective}px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
              }}
            >
              {sourceNode.data.imageUrl ? (
                <img
                  src={resolveBrowserMediaUrl(sourceNode.data.imageUrl)}
                  alt="预览"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center bg-slate-200 text-sm text-slate-400">
                  无图片预览
                </div>
              )}
            </div>
          </div>

          {/* 右侧：参数控制 */}
          <div className="w-[300px] space-y-4 border-l border-slate-200 p-5">
            {/* 水平角度 */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-slate-600">水平角度</label>
                <span className="text-xs tabular-nums text-slate-400">{horizontalAngle}°</span>
              </div>
              <input
                type="range"
                min={-60}
                max={60}
                value={horizontalAngle}
                onChange={(e) => setHorizontalAngle(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
              <div className="flex justify-between text-[10px] text-slate-400">
                <span>左 60°</span>
                <span>右 60°</span>
              </div>
            </div>

            {/* 俯仰角度 */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-slate-600">俯仰角度</label>
                <span className="text-xs tabular-nums text-slate-400">{verticalAngle}°</span>
              </div>
              <input
                type="range"
                min={-45}
                max={45}
                value={verticalAngle}
                onChange={(e) => setVerticalAngle(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
              <div className="flex justify-between text-[10px] text-slate-400">
                <span>俯 45°</span>
                <span>仰 45°</span>
              </div>
            </div>

            {/* 镜头距离 */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-xs font-medium text-slate-600">镜头距离</label>
                <span className="text-xs tabular-nums text-slate-400">{lensDistance}</span>
              </div>
              <input
                type="range"
                min={1}
                max={10}
                step={0.5}
                value={lensDistance}
                onChange={(e) => setLensDistance(Number(e.target.value))}
                className="w-full accent-blue-500"
              />
            </div>

            {/* 镜头类型 */}
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">镜头类型</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setLensType('standard')}
                  className={`flex-1 rounded-lg py-2 text-xs transition ${
                    lensType === 'standard'
                      ? 'bg-blue-500 text-white'
                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  标准
                </button>
                <button
                  type="button"
                  onClick={() => setLensType('wide')}
                  className={`flex-1 rounded-lg py-2 text-xs transition ${
                    lensType === 'wide'
                      ? 'bg-blue-500 text-white'
                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  广角
                </button>
              </div>
            </div>

            {/* 重置 */}
            <button
              type="button"
              onClick={resetParams}
              className="w-full rounded-lg border border-slate-200 py-2 text-xs text-slate-600 transition hover:bg-slate-50"
            >
              重置参数
            </button>
          </div>
        </div>

        {/* 底部操作 */}
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <span className="text-xs text-slate-400">
            角度参数将作为提示词增强传入图片生成
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm text-slate-600 transition hover:bg-slate-100"
            >
              取消
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-1.5 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Wand2 className="h-4 w-4" />
              {generating ? '生成中...' : '生成多角度'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
